// Reusable Text Input Component with AI Refinement Feature
// Can be used for both textarea and input fields

import React, { useState, useRef } from 'react';
import LlmService, { LlmResponse } from '../../features/dashboard/channels/chat/api/llmApi';
import {
  uploadImage,
  validateImageFile,
  getImageFilesFromClipboardEvent,
} from '../../features/dashboard/channels/chat/components/shared/attachements/imageUploadUtils';
import { ImageAttachment } from '../../features/dashboard/channels/chat/components/shared/attachements/conversationalTypes';
import { TokenManager } from '../lib/apiClient';

export interface TextInputWithAIProps {
  // Value and onChange handlers
  value: string;
  onChange: (value: string) => void;
  
  // Component ID for LLM processing (e.g., 'gen_initialPrompt', 'gen_userflow')
  componentId: string;
  
  // Input type: 'textarea' or 'input'
  type?: 'textarea' | 'input';
  
  // Standard input props
  id?: string;
  name?: string;
  placeholder?: string;
  required?: boolean;
  disabled?: boolean;
  rows?: number; // For textarea
  maxLength?: number;
  
  // Styling
  className?: string;
  inputClassName?: string;
  buttonClassName?: string;
  
  // Label (optional)
  label?: string;
  labelClassName?: string;
  
  // Error and success messages
  error?: string | null;
  showSuccessMessage?: boolean;
  
  // Callbacks
  onRefineStart?: () => void;
  onRefineComplete?: (response: LlmResponse) => void;
  onRefineError?: (error: Error) => void;
  onProgress?: (update: string) => void;
  
  // Custom button text and icon
  buttonText?: string;
  showButtonIcon?: boolean;
  iconOnly?: boolean; // Show only icon without text (default: true)
  
  // Auto-resize for textarea
  autoResize?: boolean;
  minHeight?: string;
  maxHeight?: string;
  
  // Image upload support
  enableImageUpload?: boolean;
  onAttachmentsChange?: (attachments: ImageAttachment[]) => void;
}

const TextInputWithAI: React.FC<TextInputWithAIProps> = ({
  value,
  onChange,
  componentId,
  type = 'textarea',
  id,
  name,
  placeholder,
  required = false,
  disabled = false,
  rows = 4,
  maxLength,
  className = '',
  inputClassName = '',
  buttonClassName = '',
  label,
  labelClassName = '',
  error,
  showSuccessMessage = true,
  onRefineStart,
  onRefineComplete,
  onRefineError,
  onProgress,
  buttonText,
  showButtonIcon = true,
  iconOnly = true, // Default to icon-only
  autoResize = false,
  minHeight,
  maxHeight,
  enableImageUpload = false,
  onAttachmentsChange,
}) => {
  const [isLlmProcessing, setIsLlmProcessing] = useState<boolean>(false);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [progressUpdate, setProgressUpdate] = useState<string | null>(null);
  const [attachments, setAttachments] = useState<ImageAttachment[]>([]);
  const [uploading, setUploading] = useState<boolean>(false);
  const [imageErrors, setImageErrors] = useState<Set<number>>(new Set());
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Handle AI refinement
  const handleAiRefine = async () => {
    if (!componentId) {
      console.error("Component ID is required");
      onRefineError?.(new Error('Component ID is missing'));
      return;
    }

    if (!value.trim()) {
      onRefineError?.(new Error('Please enter some text to refine.'));
      return;
    }

    setIsLlmProcessing(true);
    setSuccessMessage(null);
    setProgressUpdate("Starting request...");
    onRefineStart?.();

    try {
      console.log(`Refining text with ${componentId}...`);
      
      // Process the text with the LLM service
      const response = await LlmService.processWithLlm(
        componentId,
        value,
        undefined, // No project ID for refinement
        false, // Don't save to database
        (update: string) => {
          console.log("Progress update received:", update);
          setProgressUpdate(update);
          onProgress?.(update);
        }
      );

      console.log("Refinement response received:", response);

      // Update the value with the refined text
      onChange(response.result);

      // Show success message with model name from the response
      if (showSuccessMessage) {
        const modelName = response.modelName || response.modelId || 'AI Model';
        setSuccessMessage(`Refined with ${modelName}`);
      }

      onRefineComplete?.(response);

      // Clear success message after 4 seconds
      setTimeout(() => {
        setSuccessMessage(null);
        setProgressUpdate(null);
      }, 4000);
    } catch (err: any) {
      console.error('LLM processing error:', err);
      const error = err instanceof Error ? err : new Error('Failed to refine text with AI. Please try again.');
      onRefineError?.(error);
      setProgressUpdate(null);
    } finally {
      setIsLlmProcessing(false);
    }
  };

  // Handle textarea auto-resize
  const handleTextareaInput = (e: React.FormEvent<HTMLTextAreaElement>) => {
    if (autoResize) {
      const target = e.target as HTMLTextAreaElement;
      target.style.height = 'auto';
      const newHeight = Math.min(
        target.scrollHeight,
        maxHeight ? parseInt(maxHeight) : Infinity
      );
      target.style.height = `${Math.max(
        newHeight,
        minHeight ? parseInt(minHeight) : 48
      )}px`;
    }
  };

  const processImageFiles = async (files: File[]) => {
    if (files.length === 0) return;

    setUploading(true);
    const newAttachments: ImageAttachment[] = [];
    const idToken = TokenManager.getIdToken();
    const userId = TokenManager.getUserId();

    for (const file of files) {
      const validation = validateImageFile(file);
      if (!validation.valid) {
        alert(validation.error);
        continue;
      }

      try {
        const uploadResult = await uploadImage(file, {
          idToken: idToken || undefined,
          userId: userId || undefined,
          projectId: undefined,
          useS3: true,
        });
        newAttachments.push({
          kind: 'image',
          mime: uploadResult.mime,
          url: uploadResult.url,
          name: uploadResult.name,
          path: uploadResult.key,
        });
      } catch (error) {
        console.error('Error uploading image:', error);
        alert(`Failed to upload ${file.name}`);
      }
    }

    if (newAttachments.length > 0) {
      const updatedAttachments = [...attachments, ...newAttachments];
      setAttachments(updatedAttachments);
      onAttachmentsChange?.(updatedAttachments);
    }
    setUploading(false);
  };

  const handleImageUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (!files || files.length === 0) return;
    await processImageFiles(Array.from(files));
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handlePaste = async (event: React.ClipboardEvent) => {
    if (!enableImageUpload || uploading || disabled || isLlmProcessing) return;
    const imageFiles = getImageFilesFromClipboardEvent(event.nativeEvent);
    if (imageFiles.length === 0) return;
    event.preventDefault();
    await processImageFiles(imageFiles);
  };

  const removeAttachment = (index: number) => {
    const updatedAttachments = attachments.filter((_, i) => i !== index);
    setAttachments(updatedAttachments);
    onAttachmentsChange?.(updatedAttachments);
  };

  // Determine button position based on input type
  const buttonPosition = type === 'textarea' ? 'absolute top-3 right-3' : 'absolute top-1/2 right-3 -translate-y-1/2';

  // Adjust padding based on icon-only mode and image upload
  const hasImageUpload = enableImageUpload;
  const inputPaddingRight = iconOnly 
    ? (hasImageUpload ? 'pr-24' : 'pr-12')
    : (hasImageUpload ? 'pr-32' : 'pr-20');
  const inputPaddingBottom = type === 'textarea' && hasImageUpload ? 'pb-12' : '';
  
  // Default input styles (focus: soft inner violet wash reads as a subtle “cursor glow” on dark UI)
  const defaultInputStyles = type === 'textarea'
    ? `w-full px-4 py-3 ${inputPaddingRight} ${inputPaddingBottom} bg-white/10 border border-white/20 rounded-xl text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-purple-500/50 focus:border-purple-400/50 focus:shadow-[inset_0_0_48px_-12px_rgba(139,92,246,0.12)] transition-all duration-300 backdrop-blur-sm resize-none`
    : `w-full px-4 py-3 ${inputPaddingRight} bg-white/10 border border-white/20 rounded-xl text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-purple-500/50 focus:border-purple-400/50 transition-all duration-300 backdrop-blur-sm`;

  // Button size based on icon-only mode
  const buttonSize = iconOnly ? 'p-2' : 'px-3 py-1.5';
  const buttonTextSize = iconOnly ? '' : 'text-xs font-semibold';
  
  // Default button styles
  const defaultButtonStyles = `
    ${buttonPosition} ${buttonSize} ${buttonTextSize} rounded-lg
    transition-all duration-300 transform
    ${value.trim()
      ? 'bg-gradient-to-r from-purple-600 to-purple-500 hover:from-purple-700 hover:to-purple-600 text-white shadow-lg hover:shadow-purple-500/40 hover:scale-110 hover:shadow-2xl hover:rotate-2 active:scale-95'
      : 'bg-purple-300/30 text-purple-200 border border-purple-400/30 cursor-not-allowed'
    }
    ${isLlmProcessing ? 'animate-bounce scale-105 shadow-purple-500/50' : ''}
    ${disabled ? 'opacity-50 cursor-not-allowed' : ''}
  `;

  return (
    <div className={`space-y-2 ${className}`}>
      {/* Label */}
      {label && (
        <label
          htmlFor={id}
          className={`block text-sm font-medium text-white ${labelClassName}`}
        >
          {label}
          {required && <span className="text-red-400 ml-1">*</span>}
        </label>
      )}

      {/* Attachments preview */}
      {enableImageUpload && attachments.length > 0 && (
        <div className="mb-2 flex flex-wrap gap-2">
          {attachments.map((attachment, idx) => (
            <div key={idx} className="relative inline-block">
              {!imageErrors.has(idx) ? (
                <img 
                  src={attachment.url} 
                  alt={attachment.name || 'Attachment'}
                  className="w-16 h-16 object-cover rounded-lg border border-gray-600/50 shadow-sm"
                  onError={() => setImageErrors(prev => new Set(prev).add(idx))}
                />
              ) : (
                <div className="w-16 h-16 rounded-lg border border-gray-600/50 bg-gray-700/50 flex items-center justify-center">
                  <svg className="w-6 h-6 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                  </svg>
                </div>
              )}
              <button
                onClick={() => {
                  removeAttachment(idx);
                  setImageErrors(prev => {
                    const newSet = new Set(prev);
                    newSet.delete(idx);
                    return newSet;
                  });
                }}
                className="absolute -top-1 -right-1 bg-red-500 text-white rounded-full w-5 h-5 flex items-center justify-center text-xs hover:bg-red-600 transition-colors shadow-lg"
                title="Remove attachment"
              >
                ×
              </button>
            </div>
          ))}
        </div>
      )}

      {/* Input Container */}
      <div className="relative">
        {type === 'textarea' ? (
          <textarea
            id={id}
            name={name}
            value={value}
            onChange={(e) => onChange(e.target.value)}
            onPaste={handlePaste}
            onInput={handleTextareaInput}
            placeholder={placeholder}
            required={required}
            disabled={disabled || isLlmProcessing}
            rows={rows}
            maxLength={maxLength}
            className={`${defaultInputStyles} ${inputClassName} ${error ? 'border-red-400/50 focus:ring-red-500/50' : ''}`}
            style={{
              minHeight: minHeight || '48px',
              maxHeight: maxHeight || 'none',
            }}
          />
        ) : (
          <input
            type="text"
            id={id}
            name={name}
            value={value}
            onChange={(e) => onChange(e.target.value)}
            onPaste={handlePaste}
            placeholder={placeholder}
            required={required}
            disabled={disabled || isLlmProcessing}
            maxLength={maxLength}
            className={`${defaultInputStyles} ${inputClassName} ${error ? 'border-red-400/50 focus:ring-red-500/50' : ''}`}
          />
        )}

        {/* Image upload button */}
        {enableImageUpload && (
          <button
            type="button"
            onClick={() => fileInputRef.current?.click()}
            disabled={uploading || disabled || isLlmProcessing}
            className={`absolute ${type === 'textarea' ? 'bottom-3 left-3' : 'top-1/2 right-12 -translate-y-1/2'} p-3 text-gray-400 hover:text-white disabled:opacity-50 disabled:cursor-not-allowed transition-colors`}
            title="Upload image or paste from clipboard (Ctrl/Cmd+V)"
          >
            {uploading ? (
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-white"></div>
            ) : (
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
            )}
          </button>
        )}

        {/* AI Refine Button */}
        <button
          type="button"
          onClick={handleAiRefine}
          disabled={disabled || isLlmProcessing || !value.trim()}
          className={`${defaultButtonStyles} ${buttonClassName}`}
          title="Refine with AI"
        >
          {isLlmProcessing ? (
            <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
          ) : (
            <span className="flex items-center justify-center">
              {iconOnly ? (
                // Icon only - show sparkles
                <span className="text-yellow-300 text-lg leading-none">
                  <span className="inline-block animate-star-shimmer">✨</span>
                </span>
              ) : (
                // Icon with text
                <span className="flex items-center space-x-1">
                  {showButtonIcon && <span className="animate-star-shimmer text-yellow-300">✨</span>}
                  {buttonText && <span>{buttonText}</span>}
                </span>
              )}
            </span>
          )}
        </button>

        {/* Hidden file input */}
        {enableImageUpload && (
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            multiple
            onChange={handleImageUpload}
            className="hidden"
          />
        )}
      </div>

      {/* Status Messages */}
      <div className="space-y-2">
        {error && (
          <div className="p-3 bg-red-500/10 border border-red-400/30 rounded-xl backdrop-blur-sm">
            <p className="text-sm text-red-300 flex items-center">
              <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              {error}
            </p>
          </div>
        )}

        {successMessage && showSuccessMessage && (
          <div className="p-3 bg-green-500/10 border border-green-400/30 rounded-xl backdrop-blur-sm">
            <p className="text-sm text-green-300 flex items-center">
              <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              {successMessage}
            </p>
          </div>
        )}

        {progressUpdate && (
          <div className="p-3 bg-purple-500/10 border border-purple-400/30 rounded-xl backdrop-blur-sm">
            <p className="text-sm text-purple-300 flex items-center">
              <svg className="w-4 h-4 mr-2 animate-spin" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              {progressUpdate}
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default TextInputWithAI;

